import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {CreateCustomFieldDropdownContainer} from '../../../../../../../../../src/components/common/dropdowns/customFields/createCustomFieldDropdownContainer';
import {CustomFieldDropdown} from '../../../../../../../../../src/components/common/dropdowns/customFields/customFieldDropdown';
import {MappingRowField} from '../../../../../../../../../src/components/common/mapping/mappingRowField';
import {isCustomFieldEnumType} from '../../../../../../../../../src/helpers/customFieldHelpers';
import {renderCustomFieldType} from '../../../../../../../../../src/helpers/customFieldViewHelpers';
import {
  CrmSyncCardDefaultFieldIdsEnum,
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {CustomFieldModel, CustomFieldsEnum} from '../../../../../../../../../src/models/api/customFieldModel';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {
  computeCustomFieldResourceType,
  renderFrontAccountDefaultFieldName,
  renderFrontCardDefaultFieldName,
} from '../crmSyncsPageConfig/crmSyncsPageConfigHelpers';

/*
 * Props.
 */

interface FrontFieldDropdownWrapperProps {
  source: CrmSyncSourcesEnum;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  types: ReadonlyArray<string>;
  customFields: ReadonlyArray<CustomFieldModel>;
  selectedFieldId?: number;
  selectedFieldIcon?: IconName;
  computeFrontFieldIcon: (fieldId: number) => IconName | undefined;
  frontCustomFieldName?: ReactNode;
  crmCustomFieldName?: string;
  selectedCrmFieldType?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isFrontCustomFieldMissing?: boolean;
  allSelectedFieldIds: ReadonlyArray<number>;
  onSelect: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledMappingPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const FrontFieldDropdownWrapper: FC<FrontFieldDropdownWrapperProps> = (props) => {
  const {
    source,
    types,
    selectedFieldId,
    selectedFieldIcon,
    frontCustomFieldName,
    crmCustomFieldName,
    isReadOnly,
    isDisabled,
    allSelectedFieldIds,
    isFrontCustomFieldMissing,
    customFields,
    resourceType,
    selectedCrmFieldType,
    computeFrontFieldIcon,
    onSelect,
  } = props;
  const config = useCrmPageConfig(source);

  const hasNoCustomFieldsToShow = customFields.length === 0;
  const [isShowingCreateDropdown, setIsShowingCreateDropdown] = useState(false);
  const [fieldName, setFieldName] = useState(crmCustomFieldName);

  const onCancelClick = (popoverProps: PopoverTransferProps) => {
    if (hasNoCustomFieldsToShow) {
      popoverProps.onRequestClose();
    } else {
      setIsShowingCreateDropdown(false);
    }
  };

  const onCreateClick = (input?: string) => {
    if (input) {
      setFieldName(input);
    } else {
      setFieldName(crmCustomFieldName);
    }

    setIsShowingCreateDropdown(true);
  };

  const resourceConfig = config[resourceType];
  if (!resourceConfig) {
    return null;
  }

  return (
    <PopoverCoordinator
      isDisabled={isReadOnly || isDisabled}
      renderButton={() => (
        <MappingRowField
          iconName={selectedFieldIcon}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          hasError={isFrontCustomFieldMissing}
          message={
            resourceConfig?.syncMapping.renderSpecialFieldMessage
              ? resourceConfig.syncMapping.renderSpecialFieldMessage(selectedFieldId)
              : null
          }
          errorMessage={maybeRenderErrorMessage(isFrontCustomFieldMissing)}
        >
          {frontCustomFieldName ?? renderPlaceholder()}
        </MappingRowField>
      )}
      renderModal={(popoverProps) => {
        if (hasNoCustomFieldsToShow || isShowingCreateDropdown) {
          return (
            <CreateCustomFieldDropdownContainer
              popoverProps={popoverProps}
              types={types.filter(isCustomFieldEnumType)}
              fieldName={fieldName}
              customFieldResourceType={computeCustomFieldResourceType(resourceType)}
              renderTypesDisabledTooltip={(type) =>
                renderTypesDisabledTooltip(config.displayName, type, selectedCrmFieldType)
              }
              renderTypesDropdownItemDisabledTooltip={() =>
                renderTypesDropdownItemDisabledTooltip(config.displayName)
              }
              renderDropdownTitle={() => renderCreateDropdownTitle(resourceType)}
              isShowingBackButton={!hasNoCustomFieldsToShow}
              onCancelClick={() => onCancelClick(popoverProps)}
              onSelect={(customFieldId) => {
                onSelect(customFieldId);
                setIsShowingCreateDropdown(false);
                popoverProps.onRequestClose();
              }}
            />
          );
        }
        return (
          <CustomFieldDropdown
            popoverProps={popoverProps}
            customFieldResourceType={computeCustomFieldResourceType(resourceType)}
            selectedCustomFieldId={selectedFieldId}
            allSelectedCustomFieldIds={allSelectedFieldIds}
            onSelect={onSelect}
            customFields={customFields}
            onCreateClick={onCreateClick}
            shouldAllowAlreadySelectedFields
            renderBannerText={() => renderBannerText(config.displayName)}
            renderCustomFieldName={(customField) => renderCustomFieldName(resourceType, customField)}
            renderSpecialCustomFieldType={(customField) =>
              renderSpecialCustomFieldType(customField, resourceType)
            }
            renderCustomFieldIcon={(customField) => computeFrontFieldIcon(customField.id)}
          />
        );
      }}
    />
  );
};

/*
 * Helpers.
 */

function maybeRenderErrorMessage(isFrontCustomFieldMissing?: boolean) {
  if (!isFrontCustomFieldMissing) {
    return undefined;
  }
  return <Trans id="crm-syncs-page-mapping-custom-field-deleted">Field has been deleted in Front.</Trans>;
}

function renderPlaceholder() {
  return (
    <StyledMappingPlaceholderDiv>
      <Trans id="crm-syncs-page-mapping-custom-field-placeholder">Select Front field to map to</Trans>
    </StyledMappingPlaceholderDiv>
  );
}

function renderCreateDropdownTitle(resourceType: CrmSyncIntegrationResourceTypesEnum) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return <Trans id="crm-syncs-page-custom-field-dropdown-account-title">New account custom field</Trans>;
  }
  return <Trans id="crm-syncs-page-custom-field-dropdown-contact-title">New contact custom field</Trans>;
}

function renderTypesDropdownItemDisabledTooltip(crmDisplayName: string) {
  return (
    <Trans id="crm-syncs-page-custom-field-create-type-item-tooltip">
      Selected {crmDisplayName} Data Type cannot be mapped to this Front field type.
    </Trans>
  );
}

function renderTypesDisabledTooltip(
  crmDisplayName: string,
  type: CustomFieldsEnum,
  selectedCrmDataType?: string,
) {
  const frontCustomFieldTypeName = renderCustomFieldType(type);
  if (selectedCrmDataType) {
    return (
      <Trans id="crm-syncs-page-custom-field-create-type-disabled-tooltip-with-data-type">
        {crmDisplayName} “{selectedCrmDataType}“ Data Type can only be mapped to a Front “
        {frontCustomFieldTypeName}“ type field.
      </Trans>
    );
  }
  return (
    <Trans id="crm-syncs-page-custom-field-create-type-disabled-tooltip">
      {crmDisplayName} Data Type can only be mapped to a Front {frontCustomFieldTypeName} type field.
    </Trans>
  );
}

function renderBannerText(crmDisplayName: string) {
  return (
    <Trans id="crm-syncs-page-mapping-custom-field-dropdown-banner">
      Only showing Front fields compatible with selected {crmDisplayName} field
    </Trans>
  );
}

function renderCustomFieldName(
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  customField: CustomFieldModel,
) {
  if (customField.id < 0) {
    return resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT
      ? renderFrontAccountDefaultFieldName(customField.id)
      : renderFrontCardDefaultFieldName(customField.id);
  }
  return customField.name;
}

function renderSpecialCustomFieldType(
  customField: CustomFieldModel,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
) {
  if (
    customField.id === CrmSyncCardDefaultFieldIdsEnum.PHONE &&
    resourceType === CrmSyncIntegrationResourceTypesEnum.CARD
  ) {
    return <Trans id="custom-fields-type-phone-number">Phone number</Trans>;
  }

  if (
    customField.id === CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID &&
    resourceType === CrmSyncIntegrationResourceTypesEnum.CARD
  ) {
    return <Trans id="custom-fields-type-account">Account</Trans>;
  }

  return renderCustomFieldType(customField.type);
}
